
import React, { useState } from "react";
import { RibaStage, Project } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { FileUpload } from "./FileUpload";
import { Download, File, FolderPlus, Search, Trash } from "lucide-react";

interface StageDocumentsProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

interface Document {
  id: string;
  name: string;
  category: string;
  uploadDate: Date;
  fileUrl: string;
}

// Document categories by stage
const documentCategories: Record<number, string[]> = {
  0: ["Client Requirements", "Business Case", "Feasibility Studies", "Site Information"],
  1: ["Project Brief", "Surveys", "Programme", "Procurement"],
  2: ["Design Concepts", "Planning Documents", "Specifications", "Cost Plans"],
  3: ["Coordinated Design", "Planning Applications", "Technical Documentation", "Cost Plans", "Design Team Reports"],
  4: ["Technical Drawings", "Specifications", "Construction Information", "Regulatory Submissions", "Tender Documents"],
  5: ["Site Reports", "Construction Documents", "Quality Records", "Health & Safety", "Progress Reports"],
  6: ["Commissioning Certificates", "Building Manuals", "As-built Drawings", "Handover Documentation", "Defects Records"],
  7: ["Feedback", "Performance Reviews", "Maintenance Records", "Post-Occupancy Reports", "Sustainability Assessments"],
};

export default function StageDocuments({ project, stage, onUpdate }: StageDocumentsProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newDocument, setNewDocument] = useState<Partial<Document>>({
    name: "",
    category: "",
    uploadDate: new Date(),
  });
  
  // Get available categories for this stage
  const categories = documentCategories[stage.id] || ["General"];
  
  const handleAddDocument = (url: string) => {
    if (!newDocument.name || !newDocument.category || !url) {
      toast({
        title: "Missing information",
        description: "Please provide a name, category, and file",
        variant: "destructive",
      });
      return;
    }
    
    const doc: Document = {
      id: `doc-${Date.now()}`,
      name: newDocument.name,
      category: newDocument.category,
      uploadDate: new Date(),
      fileUrl: url,
    };
    
    setDocuments(prev => [...prev, doc]);
    
    toast({
      title: "Document added",
      description: `${doc.name} has been added to the document vault.`
    });
    
    // Reset form
    setNewDocument({
      name: "",
      category: "",
      uploadDate: new Date(),
    });
    
    setIsDialogOpen(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const handleDeleteDocument = (id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
    
    toast({
      title: "Document deleted",
      description: "The document has been removed from the vault."
    });
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  // Filter documents based on search
  const filteredDocuments = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    doc.category.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Document Vault</CardTitle>
            <CardDescription>Project documents and files</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <FolderPlus className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Document</DialogTitle>
                <DialogDescription>
                  Upload a document to the project vault.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Document Name</label>
                  <Input 
                    placeholder="Enter document name"
                    value={newDocument.name}
                    onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Select 
                    onValueChange={(value) => setNewDocument({...newDocument, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Upload File</label>
                  <FileUpload 
                    id="document-upload"
                    onUpload={(url) => {
                      if (url) {
                        handleAddDocument(url);
                      }
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {filteredDocuments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <File className="h-4 w-4 mr-2 text-blue-500" />
                        <span>{doc.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{doc.category}</TableCell>
                    <TableCell>{doc.uploadDate.toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <File className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              {searchTerm ? (
                <p className="text-gray-500">No documents found matching "{searchTerm}".</p>
              ) : (
                <p className="text-gray-500">No documents have been added yet.</p>
              )}
              <Button className="mt-4" variant="outline" onClick={() => setIsDialogOpen(true)}>
                Add Document
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
